import {
  useCallback,
  useRef,
  useState,
  createContext,
  useContext,
  ReactNode
} from 'react';

interface QueuedMutation<T = any> {
  fn: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (error: any) => void;
}

/**
 * Hook to queue mutations and ensure only one runs at a time
 *
 * This prevents race conditions when multiple mutations try to update
 * the same resource simultaneously by ensuring sequential execution.
 *
 * Usage:
 * ```tsx
 * const queue = useMutationQueue();
 *
 * // Queue a mutation - it will wait if another is in progress
 * const result = await queue.enqueue(() => updateQuote({ ... }));
 *
 * // Set callback to run when queue becomes empty
 * queue.setOnQueueEmpty(() => cache.refetch());
 * ```
 */
export const useMutationQueue = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const queueRef = useRef<QueuedMutation[]>([]);
  const processingRef = useRef(false);
  const onQueueEmptyRef = useRef<(() => void) | null>(null);

  const processQueue = useCallback(async () => {
    // Prevent concurrent processing
    if (processingRef.current || queueRef.current.length === 0) {
      return;
    }

    processingRef.current = true;
    setIsProcessing(true);

    // Process queue sequentially (await in loop is intentional for sequential execution)
    // eslint-disable-next-line no-await-in-loop
    while (queueRef.current.length > 0) {
      const mutation = queueRef.current.shift();
      if (!mutation) break;

      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await mutation.fn();
        mutation.resolve(result);
      } catch (error) {
        mutation.reject(error);
      }
    }

    processingRef.current = false;
    setIsProcessing(false);

    // Call onQueueEmpty callback when queue is completely empty
    if (onQueueEmptyRef.current) {
      onQueueEmptyRef.current();
    }
  }, []);

  const enqueue = useCallback(
    <T,>(fn: () => Promise<T>): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        queueRef.current.push({ fn, resolve, reject });
        processQueue();
      }),
    [processQueue]
  );

  const clear = useCallback(() => {
    queueRef.current = [];
    processingRef.current = false;
    setIsProcessing(false);
  }, []);

  const setOnQueueEmpty = useCallback((callback: (() => void) | null) => {
    onQueueEmptyRef.current = callback;
  }, []);

  const hasQueuedMutations = useCallback(
    () => queueRef.current.length > 0 || processingRef.current,
    []
  );

  return {
    enqueue,
    clear,
    setOnQueueEmpty,
    hasQueuedMutations,
    isProcessing,
    queueLength: queueRef.current.length
  };
};

// Context for sharing mutation queue across components
const MutationQueueContext = createContext<ReturnType<
  typeof useMutationQueue
> | null>(null);

interface MutationQueueProviderProps {
  children: ReactNode;
}

/**
 * Provider component that creates a shared mutation queue
 * Wrap your component tree with this to share a single queue across all mutations
 */
export const MutationQueueProvider = ({
  children
}: MutationQueueProviderProps) => {
  const queue = useMutationQueue();
  return (
    <MutationQueueContext.Provider value={queue}>
      {children}
    </MutationQueueContext.Provider>
  );
};

/**
 * Hook to access the shared mutation queue from context
 * Must be used within a MutationQueueProvider
 */
export const useSharedMutationQueue = () => {
  const context = useContext(MutationQueueContext);
  if (!context) {
    throw new Error(
      'useSharedMutationQueue must be used within MutationQueueProvider'
    );
  }
  return context;
};
